/**
 * Legal Process Hooks
 *
 * REQ-84: Privilege Shield & Subpoena Defense
 * Phase 3: Legal Process Tracking
 *
 * Hooks for accessing legal process data (subpoenas, court orders, etc.)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';

// Types matching the database schema
export type LegalProcessType =
  | 'subpoena'
  | 'court_order'
  | 'warrant'
  | 'agency_request'
  | 'preservation_letter'
  | 'informal_request';

export type LegalProcessStatus =
  | 'received'
  | 'under_review'
  | 'user_notified'
  | 'privilege_asserted'
  | 'motion_filed'
  | 'hearing_scheduled'
  | 'quashed'
  | 'appealed'
  | 'partially_complied'
  | 'fully_complied'
  | 'withdrawn'
  | 'expired';

export interface LegalProcess {
  id: string;
  target_user_ids: string[];
  process_type: LegalProcessType;
  issuing_authority: string;
  case_name: string | null;
  case_number: string | null;
  jurisdiction: string | null;
  received_date: string;
  compliance_deadline: string | null;
  status: LegalProcessStatus;
  status_notes: string | null;
  user_notified: boolean;
  user_notified_at: string | null;
  privilege_asserted: boolean;
  privilege_assertion_date: string | null;
  privileges_claimed: string[] | null;
  motion_to_quash_filed: boolean;
  motion_filed_date: string | null;
  hearing_date: string | null;
  hearing_time: string | null;
  hearing_location: string | null;
  hearing_outcome: string | null;
  compliance_required: boolean;
  compliance_scope: string | null;
  created_at: string;
}

export interface LegalProcessNotification {
  id: string;
  legal_process_id: string;
  user_id: string;
  notification_type: string;
  notification_method: string;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
  delivered: boolean;
  acknowledged: boolean;
  acknowledged_at: string | null;
  created_at: string;
}

/**
 * Check if user has any active legal process
 */
export function useHasActiveLegalProcess() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['hasActiveLegalProcess', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      // Call the database function
      const { data, error } = await supabase.rpc('user_has_active_legal_process', {
        p_user_id: user.id,
      });

      if (error) {
        // If function doesn't exist yet (before migration), return false
        if (error.message.includes('does not exist')) {
          return false;
        }
        trackSupabaseError('rpc', 'user_has_active_legal_process', error);
        throw error;
      }

      return data ?? false;
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Get user's active legal processes
 */
export function useUserLegalProcesses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['userLegalProcesses', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('legal_process_tracking')
        .select('*')
        .contains('target_user_ids', [user.id])
        .not('status', 'in', '("quashed","withdrawn","expired","fully_complied")')
        .order('compliance_deadline', { ascending: true, nullsFirst: false });

      if (error) {
        // If table doesn't exist yet (before migration), return empty array
        if (error.message.includes('does not exist')) {
          return [];
        }
        trackSupabaseError('select', 'legal_process_tracking', error);
        throw error;
      }

      return (data ?? []) as LegalProcess[];
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000,
  });
}

/**
 * Get a specific legal process by ID
 */
export function useLegalProcess(id: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['legalProcess', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('legal_process_tracking')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        trackSupabaseError('select', 'legal_process_tracking', error);
        throw error;
      }

      return data as LegalProcess;
    },
    enabled: !!id && !!user?.id,
  });
}

/**
 * Get notifications for a legal process
 */
export function useLegalProcessNotifications(legalProcessId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['legalProcessNotifications', legalProcessId, user?.id],
    queryFn: async () => {
      if (!legalProcessId || !user?.id) return [];

      const { data, error } = await supabase
        .from('legal_process_notifications')
        .select('*')
        .eq('legal_process_id', legalProcessId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.message.includes('does not exist')) {
          return [];
        }
        trackSupabaseError('select', 'legal_process_notifications', error);
        throw error;
      }

      return (data ?? []) as LegalProcessNotification[];
    },
    enabled: !!legalProcessId && !!user?.id,
  });
}

/**
 * Get unacknowledged notifications for current user
 */
export function useUnacknowledgedLegalNotifications() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['unacknowledgedLegalNotifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('legal_process_notifications')
        .select(`
          *,
          legal_process:legal_process_tracking(
            id,
            process_type,
            status,
            issuing_authority,
            compliance_deadline
          )
        `)
        .eq('user_id', user.id)
        .eq('requires_acknowledgment', true)
        .eq('acknowledged', false)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.message.includes('does not exist')) {
          return [];
        }
        trackSupabaseError('select', 'legal_process_notifications', error);
        throw error;
      }

      return data ?? [];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * Acknowledge a legal process notification
 */
export function useAcknowledgeLegalNotification() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('legal_process_notifications')
        .update({
          acknowledged: true,
          acknowledged_at: new Date().toISOString(),
          acknowledgment_ip: 'client', // Server would get real IP
        })
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) {
        trackSupabaseError('update', 'legal_process_notifications', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unacknowledgedLegalNotifications'] });
      queryClient.invalidateQueries({ queryKey: ['legalProcessNotifications'] });
    },
  });
}

/**
 * Get status history for a legal process
 */
export function useLegalProcessStatusHistory(legalProcessId: string | undefined) {
  return useQuery({
    queryKey: ['legalProcessStatusHistory', legalProcessId],
    queryFn: async () => {
      if (!legalProcessId) return [];

      const { data, error } = await supabase
        .from('legal_process_status_history')
        .select('*')
        .eq('legal_process_id', legalProcessId)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.message.includes('does not exist')) {
          return [];
        }
        trackSupabaseError('select', 'legal_process_status_history', error);
        throw error;
      }

      return data ?? [];
    },
    enabled: !!legalProcessId,
  });
}

/**
 * Format legal process type for display
 */
export function formatProcessType(type: LegalProcessType): string {
  const labels: Record<LegalProcessType, string> = {
    subpoena: 'Subpoena',
    court_order: 'Court Order',
    warrant: 'Warrant',
    agency_request: 'Agency Request',
    preservation_letter: 'Preservation Letter',
    informal_request: 'Informal Request',
  };
  return labels[type] || type;
}

/**
 * Format legal process status for display
 */
export function formatProcessStatus(status: LegalProcessStatus): string {
  const labels: Record<LegalProcessStatus, string> = {
    received: 'Received',
    under_review: 'Under Review',
    user_notified: 'User Notified',
    privilege_asserted: 'Privilege Asserted',
    motion_filed: 'Motion Filed',
    hearing_scheduled: 'Hearing Scheduled',
    quashed: 'Quashed',
    appealed: 'Appealed',
    partially_complied: 'Partially Complied',
    fully_complied: 'Fully Complied',
    withdrawn: 'Withdrawn',
    expired: 'Expired',
  };
  return labels[status] || status;
}

/**
 * Get status color for badges
 */
export function getStatusColor(status: LegalProcessStatus): string {
  const colors: Record<LegalProcessStatus, string> = {
    received: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    under_review: 'bg-blue-100 text-blue-800 border-blue-200',
    user_notified: 'bg-blue-100 text-blue-800 border-blue-200',
    privilege_asserted: 'bg-green-100 text-green-800 border-green-200',
    motion_filed: 'bg-purple-100 text-purple-800 border-purple-200',
    hearing_scheduled: 'bg-amber-100 text-amber-800 border-amber-200',
    quashed: 'bg-green-100 text-green-800 border-green-200',
    appealed: 'bg-purple-100 text-purple-800 border-purple-200',
    partially_complied: 'bg-orange-100 text-orange-800 border-orange-200',
    fully_complied: 'bg-slate-100 text-slate-800 border-slate-200',
    withdrawn: 'bg-slate-100 text-slate-800 border-slate-200',
    expired: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return colors[status] || 'bg-slate-100 text-slate-800 border-slate-200';
}
